import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { getScreams } from "../redux/actions/dataActions";
import GameSummary from './GameSummary';
import { fetchGames } from "../util/api/game";
import { fetchUserProfile } from "../util/api/user.js";
import chessslogo from "../images/chessslogo.png";
import { usePremium } from '../util/PremiumFilter';

const ViewStats = (props) => {
    // Get handle from props
    const { handle } = props;
    
    // References for scrolling elements
    const gamesScrollRef = useRef(null);
    const achievementsRef = useRef(null);

    // Add state to track which game type is selected
    const [selectedGameType, setSelectedGameType] = useState('ALL');

    // Initialize data state
    const [userData, setUserData] = useState({
        bio: props?.user?.credentials?.biography || "Chess enthusiast and strategic thinker. I enjoy analyzing complex positions and finding the best moves.",
        overallRating: 2000,
        rapidRating: 2000,
        blitzRating: 2000,
        bulletRating: 2000,
        peakRating: 2123,
        gamesPlayed: 523,
        winRate: 62,
        puzzlesCompleted: 1248,
        gameStats: {
            ALL: {
                wins: 325,
                losses: 158,
                draws: 40,
                totalGames: 523,
                winRate: "62%"
            },
            RAPID: {
                wins: 165,
                losses: 70,
                draws: 25,
                totalGames: 260,
                winRate: "63%"
            },
            BLITZ: {
                wins: 128,
                losses: 62,
                draws: 10,
                totalGames: 200,
                winRate: "64%"
            },
            BULLET: {
                wins: 32,
                losses: 26,
                draws: 5,
                totalGames: 63,
                winRate: "51%"
            }
        },
        achievements: [
            { title: "Opening Master", description: "Studied 50+ opening variations", icon: "fa-chess-board" },
            { title: "Endgame Specialist", description: "Won 25 games in endgame positions", icon: "fa-flag-checkered" },
            { title: "Blitz Champion", description: "Achieved 2000+ Blitz rating", icon: "fa-bolt" },
            { title: "Content Creator", description: "Shared 10+ annotated games", icon: "fa-pen-fancy" },
            { title: "Community Contributor", description: "Helped 30+ players improve", icon: "fa-users" }
        ],
        recentResults: [
            { result: "Win", opponent: "ChessMaster99", rating: "+8" },
            { result: "Win", opponent: "KnightRider", rating: "+5" },
            { result: "Loss", opponent: "GrandmasterFlash", rating: "-7" },
            { result: "Win", opponent: "QueenGambit", rating: "+9" },
            { result: "Draw", opponent: "RookSolid", rating: "0" },
        ]
    });

    const [expand, setExpand] = useState(false);

    // Fetch user's profile
    const fetchUser = async (userHandle) => {
        try {
            const result = await fetchUserProfile(userHandle);
            setUserData(prevData => ({
                ...prevData,
                ...result.user
            }));
        } catch (error) {
            
        }
    };
      
    useEffect(() => {
        if (handle) {
            fetchUser(handle);
        }
    }, [handle]);

    const onChange = () => {
        setExpand(!expand);
    }

    // Sample games for the GameSummary container
    const [games, setGames] = useState([]);
    const [isLoadingGames, setIsLoadingGames] = useState(true);

    // Mock function to simulate loading games
    useEffect(() => {
        const loadGames = async () => {
            setIsLoadingGames(true);
            try {
                // In a real implementation, this would fetch actual games from the API
                const mockGames = await fetchGames(8);
                setGames(mockGames || []);
            } catch (error) {
                console.error("Error loading games:", error);
            } finally {
                setIsLoadingGames(false);
            }
        };

        loadGames();
    }, []);

    // Function to scroll the games container
    const scrollGames = (direction) => {
        if (gamesScrollRef.current) {
            const scrollAmount = direction === 'left' ? -400 : 400;
            gamesScrollRef.current.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    // Function to scroll achievements
    const scrollAchievements = (direction) => {
        if (achievementsRef.current) {
            const scrollAmount = direction === 'left' ? -300 : 300;
            achievementsRef.current.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    // Helper function to get current stats based on selected game type
    const getCurrentStats = () => {
        return userData.gameStats[selectedGameType] || userData.gameStats.ALL;
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '10px',
                borderRadius: '15px',
                color: '#fff',
                minHeight: '100vh',
                boxShadow: 'inset 0 0 50px rgba(0,0,0,0.5)',
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 250px)',
            }}
        >
            {/* Trophy Room Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '30px',
                padding: '20px',
                borderBottom: '3px solid gold',
                background: 'rgba(0,0,0,0.3)',
                borderRadius: '10px'
            }}>
                <img src={chessslogo} alt="Chess Logo" style={{ width: '60px', height: 'auto', marginRight: '20px' }} />
                <h1 style={{
                    fontSize: '42px',
                    margin: 0,
                    color: 'gold',
                    fontWeight: 'bold',
                    textShadow: '0 0 10px rgba(255, 215, 0, 0.3)',
                    fontFamily: 'Arial, sans-serif',
                    letterSpacing: '2px'
                }}>PLAYER TROPHY ROOM</h1>
                <img src={chessslogo} alt="Chess Logo" style={{ width: '60px', height: 'auto', marginLeft: '20px' }} />
            </div>

            {/* Main Stats Area */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '30px',
                marginBottom: '30px'
            }}>
                {/* Left Column - Overall Rating & Bio */}
                <div style={{
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px'
                }}>
                    {/* Overall Rating Trophy */}
                    <div style={{
                        padding: '30px',
                        background: 'linear-gradient(135deg, #000000 0%, #222222 100%)',
                        borderRadius: '15px',
                        border: '5px solid gold',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.4), 0 0 30px rgba(255,215,0,0.2)',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '-25px',
                            left: '-25px',
                            width: '80px',
                            height: '80px',
                            background: 'gold',
                            transform: 'rotate(45deg)',
                            zIndex: '1'
                        }}></div>
                        <i className="fa-solid fa-trophy" style={{
                            position: 'absolute',
                            top: '10px',
                            left: '10px',
                            fontSize: '24px',
                            color: 'black',
                            zIndex: '2'
                        }}></i>

                        <h2 style={{ fontSize: '28px', color: 'white', marginBottom: '20px' }}>OVERALL RATING</h2>
                        <div style={{ fontSize: '76px', fontWeight: 'bold', color: 'gold', marginBottom: '10px' }}>
                            {userData.overallRating}
                        </div>
                        <div style={{
                            fontSize: '18px',
                            color: 'rgba(255,255,255,0.7)',
                            marginBottom: '15px',
                            padding: '0 20px'
                        }}>
                            Factors in Overall Community Contributions and Accuracy
                        </div>
                        <div style={{
                            display: 'inline-block',
                            padding: '8px 20px',
                            background: 'rgba(255,215,0,0.2)',
                            color: 'gold',
                            borderRadius: '20px',
                            fontSize: '20px',
                            fontWeight: 'bold'
                        }}>
                            TOP 1%
                        </div>
                    </div>
                </div>

                {/* Right Column - Rating Cards & Game Stats */}
                <div style={{
                    flex: '1.2',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                }}>
                    {/* Game Type Selection Buttons */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px'
                    }}>
                        <h2 style={{
                            fontSize: '22px',
                            color: 'white',
                            borderBottom: '2px solid gold',
                            paddingBottom: '10px',
                            textAlign: 'center',
                            margin: '0 0 10px 0'
                        }}>
                            RATING BY GAME TYPE
                            <span style={{
                                fontSize: '16px',
                                marginLeft: '10px',
                                fontStyle: 'italic',
                                color: 'rgba(255,255,255,0.7)'
                            }}>
                                (Click to view stats)
                            </span>
                        </h2>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '15px'
                        }}>
                            {/* All Games Button */}
                            <button
                                onClick={() => setSelectedGameType('ALL')}
                                style={{
                                    flex: '1',
                                    padding: '20px',
                                    background: selectedGameType === 'ALL'
                                        ? 'linear-gradient(135deg, #5e35b1 0%, #7e57c2 100%)'
                                        : 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                                    borderRadius: '15px',
                                    textAlign: 'center',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                                    border: selectedGameType === 'ALL'
                                        ? '3px solid white'
                                        : '1px solid rgba(255,255,255,0.1)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    transform: selectedGameType === 'ALL'
                                        ? 'scale(1.05)'
                                        : 'scale(1)',
                                    outline: 'none'
                                }}
                            >
                                <h3 style={{ fontSize: '22px', color: 'white', marginBottom: '10px' }}>ALL GAMES</h3>
                                <div style={{ fontSize: '42px', fontWeight: 'bold', color: 'white' }}>
                                    {userData.gameStats?.ALL?.totalGames || 0}
                                </div>
                                <div style={{
                                    marginTop: '10px',
                                    background: 'rgba(0,0,0,0.3)',
                                    padding: '5px 10px',
                                    borderRadius: '15px',
                                    display: 'inline-block',
                                    fontSize: '16px',
                                    color: 'white'
                                }}>
                                    WR: {userData.gameStats?.ALL?.winRate || "0%"}
                                </div>
                            </button>

                            {/* Rapid Rating Button */}
                            <button
                                onClick={() => setSelectedGameType('RAPID')}
                                style={{
                                    flex: '1',
                                    padding: '20px',
                                    background: selectedGameType === 'RAPID'
                                        ? 'linear-gradient(135deg, #00796b 0%, #26a69a 100%)'
                                        : 'linear-gradient(135deg, #004d40 0%, #26a69a 100%)',
                                    borderRadius: '15px',
                                    textAlign: 'center',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                                    border: selectedGameType === 'RAPID'
                                        ? '3px solid white'
                                        : '1px solid rgba(255,255,255,0.1)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    transform: selectedGameType === 'RAPID'
                                        ? 'scale(1.05)'
                                        : 'scale(1)',
                                    outline: 'none'
                                }}
                            >
                                <h3 style={{ fontSize: '22px', color: 'white', marginBottom: '10px' }}>RAPID</h3>
                                <div style={{ fontSize: '42px', fontWeight: 'bold', color: 'white' }}>
                                    {userData.rapidRating}
                                </div>
                                <div style={{
                                    marginTop: '10px',
                                    background: 'rgba(0,0,0,0.3)',
                                    padding: '5px 10px',
                                    borderRadius: '15px',
                                    display: 'inline-block',
                                    fontSize: '16px',
                                    color: 'white'
                                }}>
                                    [TOP 5%]
                                </div>
                            </button>

                            {/* Blitz Rating Button */}
                            <button
                                onClick={() => setSelectedGameType('BLITZ')}
                                style={{
                                    flex: '1',
                                    padding: '20px',
                                    background: selectedGameType === 'BLITZ'
                                        ? 'linear-gradient(135deg, #d84315 0%, #ff7043 100%)'
                                        : 'linear-gradient(135deg, #bf360c 0%, #ff7043 100%)',
                                    borderRadius: '15px',
                                    textAlign: 'center',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                                    border: selectedGameType === 'BLITZ'
                                        ? '3px solid white'
                                        : '1px solid rgba(255,255,255,0.1)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    transform: selectedGameType === 'BLITZ'
                                        ? 'scale(1.05)'
                                        : 'scale(1)',
                                    outline: 'none'
                                }}
                            >
                                <h3 style={{ fontSize: '22px', color: 'white', marginBottom: '10px' }}>BLITZ</h3>
                                <div style={{ fontSize: '42px', fontWeight: 'bold', color: 'white' }}>
                                    {userData.blitzRating}
                                </div>
                                <div style={{
                                    marginTop: '10px',
                                    background: 'rgba(0,0,0,0.3)',
                                    padding: '5px 10px',
                                    borderRadius: '15px',
                                    display: 'inline-block',
                                    fontSize: '16px',
                                    color: 'white'
                                }}>
                                    [TOP 5%]
                                </div>
                            </button>

                            {/* Bullet Rating Button */}
                            <button
                                onClick={() => setSelectedGameType('BULLET')}
                                style={{
                                    flex: '1',
                                    padding: '20px',
                                    background: selectedGameType === 'BULLET'
                                        ? 'linear-gradient(135deg, #f57c00 0%, #ffca28 100%)'
                                        : 'linear-gradient(135deg, #ff6f00 0%, #ffca28 100%)',
                                    borderRadius: '15px',
                                    textAlign: 'center',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                                    border: selectedGameType === 'BULLET'
                                        ? '3px solid white'
                                        : '1px solid rgba(255,255,255,0.1)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    transform: selectedGameType === 'BULLET'
                                        ? 'scale(1.05)'
                                        : 'scale(1)',
                                    outline: 'none'
                                }}
                            >
                                <h3 style={{ fontSize: '22px', color: selectedGameType === 'BULLET' ? 'white' : 'black', marginBottom: '10px' }}>BULLET</h3>
                                <div style={{ fontSize: '42px', fontWeight: 'bold', color: selectedGameType === 'BULLET' ? 'white' : 'black' }}>
                                    {userData.bulletRating}
                                </div>
                                <div style={{
                                    marginTop: '10px',
                                    background: 'rgba(0,0,0,0.3)',
                                    padding: '5px 10px',
                                    borderRadius: '15px',
                                    display: 'inline-block',
                                    fontSize: '16px',
                                    color: 'white'
                                }}>
                                    [TOP 5%]
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Game Stats Cards */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '15px'
                    }}>
                        {/* Games Played */}
                        <div style={{
                            flex: '1',
                            padding: '20px',
                            background: 'linear-gradient(135deg, #311b92 0%, #5e35b1 100%)',
                            borderRadius: '15px',
                            textAlign: 'center',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <i className="fa-solid fa-chess" style={{ fontSize: '36px', color: 'white', marginBottom: '10px' }}></i>
                            <h3 style={{ fontSize: '18px', color: 'white', marginBottom: '5px' }}>GAMES PLAYED</h3>
                            <div style={{ fontSize: '42px', fontWeight: 'bold', color: 'white' }}>
                                {getCurrentStats()?.totalGames || 0}
                            </div>
                        </div>

                        {/* Win Rate */}
                        <div style={{
                            flex: '1',
                            padding: '20px',
                            background: 'linear-gradient(135deg, #1b5e20 0%, #4caf50 100%)',
                            borderRadius: '15px',
                            textAlign: 'center',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <i className="fa-solid fa-trophy" style={{ fontSize: '36px', color: 'white', marginBottom: '10px' }}></i>
                            <h3 style={{ fontSize: '18px', color: 'white', marginBottom: '5px' }}>WIN RATE</h3>
                            <div style={{ fontSize: '42px', fontWeight: 'bold', color: 'white' }}>
                                {getCurrentStats()?.winRate || "0%"}
                            </div>
                        </div>

                        {/* Puzzles Completed */}
                        <div style={{
                            flex: '1',
                            padding: '20px',
                            background: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)',
                            borderRadius: '15px',
                            textAlign: 'center',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <i className="fa-solid fa-puzzle-piece" style={{ fontSize: '36px', color: 'white', marginBottom: '10px' }}></i>
                            <h3 style={{ fontSize: '18px', color: 'white', marginBottom: '5px' }}>PUZZLES</h3>
                            <div style={{ fontSize: '42px', fontWeight: 'bold', color: 'white' }}>
                                {userData.puzzlesCompleted}
                            </div>
                        </div>
                    </div>

                    {/* Win/Loss/Draw Stats */}
                    <div style={{
                        padding: '20px',
                        background: 'rgba(0,0,0,0.2)',
                        borderRadius: '15px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.1)',
                    }}>
                        <h3 style={{
                            fontSize: '20px',
                            color: 'white',
                            marginBottom: '15px',
                            textAlign: 'center',
                            borderBottom: '1px solid rgba(255,255,255,0.2)',
                            paddingBottom: '10px'
                        }}>
                            {selectedGameType === 'ALL' ? 'OVERALL' : selectedGameType} RECORD SUMMARY
                        </h3>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignItems: 'center',
                            textAlign: 'center'
                        }}>
                            <div style={{ flex: '1' }}>
                                <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#4caf50' }}>
                                    {getCurrentStats()?.wins || 0}
                                </div>
                                <div style={{ fontSize: '20px', color: '#4caf50' }}>WINS</div>
                            </div>
                            <div style={{
                                width: '1px',
                                height: '80px',
                                background: 'rgba(255,255,255,0.2)'
                            }}></div>
                            <div style={{ flex: '1' }}>
                                <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#f44336' }}>
                                    {getCurrentStats()?.losses || 0}
                                </div>
                                <div style={{ fontSize: '20px', color: '#f44336' }}>LOSSES</div>
                            </div>
                            <div style={{
                                width: '1px',
                                height: '80px',
                                background: 'rgba(255,255,255,0.2)'
                            }}></div>
                            <div style={{ flex: '1' }}>
                                <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#9e9e9e' }}>
                                    {getCurrentStats()?.draws || 0}
                                </div>
                                <div style={{ fontSize: '20px', color: '#9e9e9e' }}>DRAWS</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Game Showcase Section */}
            <div style={{ marginBottom: '40px', position: 'relative' }}>
                <h2 style={{
                    fontSize: '30px',
                    color: 'gold',
                    marginBottom: '20px',
                    padding: '10px 20px',
                    borderLeft: '5px solid gold',
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: '0 10px 10px 0',
                    display: 'inline-block'
                }}>
                    <i className="fa-solid fa-chess-knight" style={{ marginRight: '10px' }}></i>
                    Game Showcase
                </h2>

                {/* Bio display */}
                <div style={{ maxWidth: '750px', fontSize: '20px', color: 'white' }}>
                    <span>Bio: {userData.biography}</span>

                    {/* Scroll buttons */}
                    <button
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '0',
                            transform: 'translateY(-50%)',
                            background: 'rgba(255,0,80,0.8)',
                            border: '2px solid gold',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: '2',
                            color: 'gold',
                            fontSize: '20px'
                        }}
                        onClick={() => scrollGames('left')}
                    >
                        <i className="fa-solid fa-chevron-left"></i>
                    </button>

                    <button
                        style={{
                            position: 'absolute',
                            top: '50%',
                            right: '0',
                            transform: 'translateY(-50%)',
                            background: 'rgba(255,0,80,0.8)',
                            border: '2px solid gold',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: '2',
                            color: 'gold',
                            fontSize: '20px'
                        }}
                        onClick={() => scrollGames('right')}
                    >
                        <i className="fa-solid fa-chevron-right"></i>
                    </button>

                    {/* Games scroll container */}
                    <div
                        ref={gamesScrollRef}
                        className="games-scroll-container"
                        style={{
                            display: 'flex',
                            overflowX: 'auto',
                            gap: '20px',
                            padding: '20px 0',
                            paddingLeft: '40px',
                            marginBottom: '20px',
                            scrollbarWidth: 'none', // Hide scrollbar for Firefox
                            msOverflowStyle: 'none', // Hide scrollbar for IE
                            '&::-webkit-scrollbar': {
                                display: 'none' // Hide scrollbar for Chrome/Safari/Opera
                            }
                        }}
                    >
                        {isLoadingGames ? (
                            <div style={{
                                minWidth: '300px',
                                height: '350px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(0,0,0,0.3)',
                                borderRadius: '15px',
                                border: '1px solid rgba(255,255,255,0.1)'
                            }}>
                                <div style={{ textAlign: 'center' }}>
                                    <img
                                        src={chessslogo}
                                        alt="Loading chess"
                                        style={{
                                            width: '75px',
                                            height: 'auto',
                                            animation: 'spin 2s linear infinite',
                                            marginBottom: '15px'
                                        }}
                                    />
                                    <div style={{ color: 'gold', fontSize: '18px' }}>Loading games...</div>
                                    <style>
                                        {`
                                        @keyframes spin {
                                            0% { transform: rotate(0deg); }
                                            100% { transform: rotate(360deg); }
                                        }
                                        `}
                                    </style>
                                </div>
                            </div>
                        ) : games.length > 0 ? (
                            games.map((game, index) => (
                                <div
                                    key={game.id || index}
                                    style={{
                                        padding: '15px',
                                        background: index === 0
                                            ? 'linear-gradient(to top, #ff0050, black, gold)'
                                            : 'rgba(0,0,0,0.3)',
                                        borderRadius: '15px',
                                        boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                                        border: index === 0
                                            ? '2px solid #ff0050'
                                            : '1px solid rgba(255,255,255,0.1)',
                                    }}
                                >
                                    <GameSummary
                                        gameData={game}
                                        boardWidth={250}
                                        onClick={() => {
                                            window.location.href = `/games/${game.id}`;
                                        }}
                                    />
                                </div>
                            ))
                        ) : (
                            <div style={{
                                minWidth: '300px',
                                height: '350px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(0,0,0,0.3)',
                                borderRadius: '15px',
                                border: '1px solid rgba(255,255,255,0.1)',
                                padding: '20px',
                                textAlign: 'center'
                            }}>
                                <div>
                                    <i className="fa-solid fa-chess-board" style={{
                                        fontSize: '50px',
                                        color: 'gold',
                                        marginBottom: '20px'
                                    }}></i>
                                    <div style={{ color: 'white', fontSize: '18px' }}>
                                        No games available to display.<br />
                                        Start playing to showcase your best games!
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    data: state.data,
    user: state.user,
});

export default connect(mapStateToProps, { getScreams })(ViewStats);
